import type { en } from "./resources/en.all.ts";
import type { enBase } from "./resources/en.ts";

/**
 * The app's copy, in the pieces the browser fetches. `en.ts` (and `da.ts`) is what every screen needs at once; each group
 * here is the copy of screens that are opened later: the top-level keys it holds, and where each language's piece is.
 * A key (`questions.title`) is in the group whose `keys` has its first part. The checks at the bottom fail to compile
 * when a key is in neither `en.ts` nor a group, or a group names a key its file does not have.
 *
 * Adding a screen's copy: put it in its own `app.<name>.{en,da}.ts`, add it to `en.all.ts` and `da.all.ts`, and
 * add a group here. (Until it has a group it belongs in `en.ts`/`da.ts`, which every screen loads.)
 */
type Fragment<K extends string> = { readonly [P in K]: unknown };
export type CopyGroup = {
  readonly keys: readonly string[];
  readonly en: () => Promise<object>;
  readonly da: () => Promise<object>;
};

function group<const K extends string>(
  keys: readonly K[],
  en: () => Promise<Fragment<K>>,
  da: () => Promise<Fragment<K>>,
): CopyGroup & { readonly keys: readonly K[] } {
  return { keys, en, da };
}

export const COPY_GROUPS = {
  questions: group(
    ["questions"],
    () => import("./resources/app.questions.en.ts").then((m) => ({ questions: m.questionsEn })),
    () => import("./resources/app.questions.da.ts").then((m) => ({ questions: m.questionsDa })),
  ),
  code: group(
    ["code"],
    () => import("./resources/app.code.en.ts").then((m) => m.appCodeEn),
    () => import("./resources/app.code.da.ts").then((m) => m.appCodeDa),
  ),
  companyLinks: group(
    ["companyLinks"],
    () => import("./resources/app.company-links.en.ts").then((m) => m.appCompanyLinksEn),
    () => import("./resources/app.company-links.da.ts").then((m) => m.appCompanyLinksDa),
  ),
  data: group(
    ["data"],
    () => import("./resources/app.data.en.ts").then((m) => m.appDataEn),
    () => import("./resources/app.data.da.ts").then((m) => m.appDataDa),
  ),
  flow: group(
    ["flow"],
    () => import("./resources/app.understanding.en.ts").then((m) => m.appUnderstandingEn),
    () => import("./resources/app.understanding.da.ts").then((m) => m.appUnderstandingDa),
  ),
  understand: group(
    ["understand", "studying"],
    () => import("./resources/app.understand.en.ts").then((m) => m.appUnderstandEn),
    () => import("./resources/app.understand.da.ts").then((m) => m.appUnderstandDa),
  ),
  staff: group(
    ["staff"],
    () => import("./resources/app.staff.en.ts").then((m) => m.appStaffEn),
    () => import("./resources/app.staff.da.ts").then((m) => m.appStaffDa),
  ),
  settings: group(
    ["settings"],
    () => import("./resources/app.settings.en.ts").then((m) => ({ settings: m.settingsEn })),
    () => import("./resources/app.settings.da.ts").then((m) => ({ settings: m.settingsDa })),
  ),
  webhooks: group(
    ["webhooks"],
    () => import("./resources/app.webhooks.en.ts").then((m) => m.appWebhooksEn),
    () => import("./resources/app.webhooks.da.ts").then((m) => m.appWebhooksDa),
  ),
  delivery: group(
    ["delivery"],
    () => import("./resources/app.delivery.en.ts").then((m) => m.appDeliveryEn),
    () => import("./resources/app.delivery.da.ts").then((m) => m.appDeliveryDa),
  ),
  lists: group(
    ["lists"],
    () => import("./resources/app.lists.en.ts").then((m) => m.appListsEn),
    () => import("./resources/app.lists.da.ts").then((m) => m.appListsDa),
  ),
  settingsMoney: group(
    ["settingsMoney"],
    () => import("./resources/app.settings-money.en.ts").then((m) => m.appSettingsMoneyEn),
    () => import("./resources/app.settings-money.da.ts").then((m) => m.appSettingsMoneyDa),
  ),
  settingsGuard: group(
    ["settingsGuard"],
    () => import("./resources/app.settings-guard.en.ts").then((m) => m.appSettingsGuardEn),
    () => import("./resources/app.settings-guard.da.ts").then((m) => m.appSettingsGuardDa),
  ),
  settingsBrand: group(
    ["settingsBrand"],
    () => import("./resources/app.settings-brand.en.ts").then((m) => m.appSettingsBrandEn),
    () => import("./resources/app.settings-brand.da.ts").then((m) => m.appSettingsBrandDa),
  ),
  pages: group(
    ["pages", "routines"],
    () => import("./resources/app.pages.en.ts").then((m) => m.appPagesEn),
    () => import("./resources/app.pages.da.ts").then((m) => m.appPagesDa),
  ),
  sharingUi: group(
    ["sharingUi"],
    () => import("./resources/app.sharing-ui.en.ts").then((m) => ({ sharingUi: m.sharingUiEn })),
    () => import("./resources/app.sharing-ui.da.ts").then((m) => ({ sharingUi: m.sharingUiDa })),
  ),
  sharing: group(
    ["sharing"],
    () => import("./resources/app.sharing.en.ts").then((m) => m.appSharingEn),
    () => import("./resources/app.sharing.da.ts").then((m) => m.appSharingDa),
  ),
} satisfies Record<string, CopyGroup>;

export type CopyGroupId = keyof typeof COPY_GROUPS;

// Every key English has is in the base file or in exactly one group.
type GroupKeys = (typeof COPY_GROUPS)[CopyGroupId]["keys"][number];
type Uncovered = Exclude<keyof typeof en, keyof typeof enBase | GroupKeys>;
export const everyKeyHasAHome: [Uncovered] extends [never] ? true : never = true;
